import { useEffect, useRef, useState } from "react";
import { Send, Loader2, MessageCircle } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

export default function BankerMeera() {
  const [messages, setMessages] = useState<any[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${BASE}api/banker/meera/history`, { headers: { "x-banker-token": token } });
        if (r.ok) setMessages(await r.json());
      } catch {}
    })();
  }, []);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, streamText]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const token = localStorage.getItem("banker_token");
    if (!token) return;
    const userMsg = { role: "user", content: input.trim(), createdAt: new Date().toISOString(), id: Date.now() };
    setMessages(m => [...m, userMsg]);
    const msgText = input.trim();
    setInput("");
    setStreaming(true); setStreamText("");

    try {
      const r = await fetch(`${BASE}api/banker/meera/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-banker-token": token },
        body: JSON.stringify({ message: msgText }),
      });
      if (!r.ok || !r.body) throw new Error("Request failed");
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buf = "", full = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const j = JSON.parse(line.slice(6));
            if (j.content) { full += j.content; setStreamText(full); }
            if (j.done) {
              setMessages(m => [...m, { role: "assistant", content: full, createdAt: new Date().toISOString(), id: Date.now() + 1 }]);
              setStreamText("");
            }
          } catch {}
        }
      }
    } catch {
      setMessages(m => [...m, { role: "assistant", content: "I'm having trouble connecting. Please try again.", createdAt: new Date().toISOString(), id: Date.now() + 1 }]);
    }
    setStreaming(false);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] max-w-3xl mx-auto">
      <div className="flex items-center gap-3 pb-4 border-b border-white/[0.08]">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center">
          <span className="text-white font-bold text-sm">M</span>
        </div>
        <div>
          <h1 className="font-bold text-lg">Meera</h1>
          <p className="text-white/50 text-xs">Research AI · Scoped to your coverage</p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto py-4 space-y-4">
        {messages.length === 0 && !streaming && (
          <div className="text-center py-12">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/25 mx-auto flex items-center justify-center mb-4">
              <MessageCircle className="w-6 h-6 text-emerald-400" />
            </div>
            <p className="text-white/70 font-medium">Ask Meera anything.</p>
            <p className="text-white/40 text-sm mt-2 max-w-md mx-auto">She knows your assigned investors, covered companies, task queue, and every artifact you've uploaded. Ask her to draft outreach, summarise a company, or flag primary-ready investors.</p>
            <div className="mt-5 grid grid-cols-1 gap-2 max-w-md mx-auto">
              {[
                "Which of my investors is closest to a primary commitment?",
                "Draft an opener email to the founder of my hottest task's company.",
                "Summarise the last 3 artifacts I uploaded for Company X.",
                "Which of my tasks has been pending longest?",
              ].map((s, i) => (
                <button key={i} onClick={() => setInput(s)} className="text-left bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] rounded-lg p-3 text-xs text-white/70">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-[#FF5C00] text-white" : "bg-white/[0.06] border border-white/[0.08] text-white/90"}`}>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</p>
            </div>
          </div>
        ))}
        {streaming && streamText && (
          <div className="flex justify-start">
            <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-white/[0.06] border border-white/[0.08]">
              <p className="text-sm whitespace-pre-wrap leading-relaxed text-white/90">{streamText}</p>
            </div>
          </div>
        )}
        {streaming && !streamText && (
          <div className="flex justify-start"><div className="bg-white/[0.06] border border-white/[0.08] rounded-2xl px-4 py-3 flex items-center gap-2 text-white/50 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Meera is thinking...</div></div>
        )}
      </div>

      <div className="border-t border-white/[0.08] pt-4">
        <div className="flex items-end gap-2">
          <textarea value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask Meera about your portfolio..."
            disabled={streaming}
            rows={1}
            className="flex-1 bg-white/[0.04] border border-white/[0.12] focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20 rounded-lg px-4 py-3 text-sm resize-none max-h-32 outline-none" />
          <button onClick={send} disabled={!input.trim() || streaming} className="bg-[#FF5C00] hover:bg-[#E55200] disabled:opacity-40 text-white p-3 rounded-lg">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
